import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { ContractStatus, ContractStatusV2 } from '@/features/contracts'
import { applyContractDisplayFilters } from '@/features/contracts/infrastructure/supabase/SupabaseContractRepository'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { VnextContractRepository } from '../application/VnextContractRepository'
import type {
  ContractConversionLink,
  VnextContractListItem,
  VnextContractKind,
  VnextListFilters,
  VnextListScope,
  VnextLocationsOverview,
} from '../domain/vnextContract'

interface PostgrestResult {
  data: unknown
  error: { message: string; code?: string } | null
  count: number | null
}

// Même correspondance que le mapper des Contrats actuels (`legacyContractMapper.ts`).
const statusFromStatut: Record<string, ContractStatus> = {
  brouillon: 'DRAFT',
  a_signer: 'PENDING',
  en_attente: 'PENDING',
  actif: 'ACTIVE',
  suspendu: 'SUSPENDED',
  expire: 'EXPIRED',
  annule: 'CANCELLED',
}

const listColumns =
  'id,numero,statut,status_v2,date_signature,sent_at,opened_since_last_send,prix,saison,billing_name,sans_facturation,contract_flow,updated_at,client:clients(prenom,nom,entreprise)'

const listRowSchema = z.object({
  id: z.uuid(),
  numero: z.string(),
  statut: z.string(),
  status_v2: z.string().nullable(),
  date_signature: z.string().nullable(),
  sent_at: z.string().nullable(),
  opened_since_last_send: z.boolean().nullable(),
  prix: z.coerce.number().nullable(),
  saison: z.string().nullable(),
  billing_name: z.string().nullable(),
  sans_facturation: z.boolean().nullish(),
  contract_flow: z.enum(['LEGACY', 'VNEXT']),
  client: z
    .object({ prenom: z.string().nullable(), nom: z.string().nullable(), entreprise: z.string().nullable() })
    .nullable(),
})

const summarySchema = z.array(
  z.object({
    contractId: z.uuid(),
    total: z.number().int(),
    ready: z.number().int(),
    assigned: z.number().int(),
    defaultContact: z.string().nullable(),
  }),
)

const contactSchema = z.object({
  source: z.enum(['CONTRACT', 'LOCATION']),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  phone: z.string().nullable(),
  roleLabel: z.string().nullable(),
})

const overviewSchema = z.object({
  contractId: z.uuid(),
  contractType: z.enum(['RESIDENTIAL', 'COMMERCIAL', 'MULTIFAMILY']),
  pricingMode: z.enum(['GLOBAL', 'PER_LOCATION']).nullable(),
  locations: z.array(
    z.object({
      id: z.uuid(),
      order: z.number().int(),
      formattedAddress: z.string(),
      line1: z.string(),
      city: z.string().nullable(),
      postalCode: z.string().nullable(),
      latitude: z.coerce.number().nullable(),
      longitude: z.coerce.number().nullable(),
      siteName: z.string().nullable(),
      siteType: z.string().nullable(),
      unitCount: z.number().int().nullable(),
      parkingSpaces: z.number().int().nullable(),
      priceCents: z.number().int().nullable(),
      validationStatus: z.enum(['VALID', 'NEEDS_CONFIRMATION']).nullable(),
      useContractDefaultContact: z.boolean(),
      contact: contactSchema.nullable(),
      route: z.object({ id: z.uuid(), name: z.string(), color: z.string() }).nullable(),
      readiness: z.object({
        position: z.boolean(),
        contact: z.boolean(),
        entry: z.boolean(),
        snowClearingZone: z.boolean(),
        status: z.enum(['NOT_STARTED', 'INCOMPLETE', 'COMPLETE']),
      }),
    }),
  ),
  totals: z.object({
    total: z.number().int(),
    ready: z.number().int(),
    toConfigure: z.number().int(),
    assigned: z.number().int(),
    unassigned: z.number().int(),
  }),
})

const createResultSchema = z.object({
  contractId: z.uuid(),
  number: z.string(),
  totalCents: z.coerce.number().int(),
  locationCount: z.number().int(),
})

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const flowRowSchema = z.object({
  id: z.uuid(),
  numero: z.string(),
  contract_flow: z.enum(['LEGACY', 'VNEXT']),
})

const contactRowSchema = z.object({
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  phone: z.string().nullable(),
  phone_secondary: z.string().nullable(),
  email: z.string().nullable(),
  role_label: z.string().nullable(),
  is_emergency: z.boolean(),
})

const readinessSchema = z.object({
  position: z.boolean(),
  contact: z.boolean(),
  entry: z.boolean(),
  snowClearingZone: z.boolean(),
  status: z.enum(['NOT_STARTED', 'INCOMPLETE', 'COMPLETE']),
})

const polygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(z.tuple([z.number(), z.number()]))),
})

const detailSchema = z.object({
  id: z.uuid(),
  contractId: z.uuid(),
  contractNumber: z.string(),
  contractType: z.enum(['COMMERCIAL', 'MULTIFAMILY']),
  contractLocked: z.boolean(),
  position: z.number().int(),
  total: z.number().int(),
  previousId: z.uuid().nullable(),
  nextId: z.uuid().nullable(),
  line1: z.string(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  postalCode: z.string().nullable(),
  formattedAddress: z.string(),
  latitude: z.coerce.number().nullable(),
  longitude: z.coerce.number().nullable(),
  validationStatus: z.enum(['VALID', 'NEEDS_CONFIRMATION']).nullable(),
  siteName: z.string().nullable(),
  siteType: z.string().nullable(),
  unitCount: z.number().int().nullable(),
  parkingSpaces: z.number().int().nullable(),
  entryCount: z.number().int().nullable(),
  priceCents: z.number().int().nullable(),
  profile: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  useContractDefaultContact: z.boolean(),
  contacts: z.array(
    z.object({
      role: z.enum(['PRIMARY', 'EMERGENCY', 'MANAGER', 'CARETAKER', 'OTHER']),
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      phone: z.string().nullable(),
      phoneSecondary: z.string().nullable(),
      email: z.string().nullable(),
      roleLabel: z.string().nullable(),
      isEmergency: z.boolean(),
    }),
  ),
  entries: z.array(
    z.object({
      label: z.string(),
      entryType: z.enum(['MAIN', 'SECONDARY', 'LOADING_DOCK', 'GARAGE', 'EMPLOYEE', 'OTHER']),
      latitude: z.coerce.number(),
      longitude: z.coerce.number(),
      detectionRadiusM: z.coerce.number(),
      enabled: z.boolean(),
      isPrimary: z.boolean(),
    }),
  ),
  legacyFlag: z.object({ latitude: z.coerce.number(), longitude: z.coerce.number() }).nullable(),
  zones: z.array(
    z.object({
      label: z.string(),
      kind: z.enum(['SNOW_CLEARING', 'SNOW_STORAGE', 'FORBIDDEN', 'PRIORITY']),
      geometry: polygonSchema,
      surfaceM2: z.coerce.number(),
    }),
  ),
  route: z.object({ id: z.uuid(), name: z.string(), color: z.string() }).nullable(),
  readiness: readinessSchema,
})

// Champs de finalisation absents tant que 20261128070000 n'est pas appliquée.
const conversionLinkSchema = z
  .object({
    number: z.string(),
    clientId: z.uuid(),
    completedAt: z.string(),
    finalizedAt: z.string().nullish(),
    finalization: z.enum(['SOURCE_CANCELLED', 'SOURCE_KEPT']).nullish(),
    cancelledInvoices: z.number().nullish(),
    transferredCents: z.number().nullish(),
    appliedCents: z.number().nullish(),
    finalizationError: z.string().nullish(),
  })
  .transform((link): ContractConversionLink => ({
    number: link.number,
    clientId: link.clientId,
    completedAt: link.completedAt,
    ...(link.finalization && link.finalizedAt
      ? {
          finalization: {
            outcome: link.finalization,
            finalizedAt: link.finalizedAt,
            cancelledInvoices: link.cancelledInvoices ?? 0,
            transferredCents: link.transferredCents ?? 0,
            appliedCents: link.appliedCents ?? 0,
          },
        }
      : {}),
    ...(link.finalizationError ? { finalizationError: link.finalizationError } : {}),
  }))
  .nullable()

const conversionStateSchema = z.object({
  draft: z
    .object({
      conversionId: z.uuid(),
      conversionType: z.enum(['TO_COMMERCIAL', 'TO_MULTIFAMILY']),
      startedAt: z.string(),
    })
    .nullable(),
  convertedTo: conversionLinkSchema,
  convertedFrom: conversionLinkSchema,
})

const writeResultSchema = z.object({ readiness: readinessSchema })

/** Retire les clés nulles : le domaine utilise des champs facultatifs, jamais `null`. */
function withoutNulls<T extends Record<string, unknown>>(value: T): { [K in keyof T]: Exclude<T[K], null> } {
  return Object.fromEntries(Object.entries(value).filter(([, item]) => item !== null)) as {
    [K in keyof T]: Exclude<T[K], null>
  }
}

function sanitizeSearch(value: string): string {
  return value
    .replace(/[%_,().'"\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
}

/** Commercial : vNext et anciens commerciaux (restés LEGACY, décision du 22 oct. 2026). */
function kindFilter(kind: VnextContractKind): string {
  return kind === 'COMMERCIAL'
    ? 'contract_type.eq.COMMERCIAL,and(contract_flow.eq.LEGACY,submission_type.eq.COMMERCIAL)'
    : 'contract_type.eq.MULTIFAMILY'
}

function nonEmpty(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim() ?? ''
  return trimmed.length > 0 ? trimmed : undefined
}

function present<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}

export function createSupabaseVnextContractRepository(client: AppSupabaseClient): VnextContractRepository {
  const db = client as unknown as SupabaseClient

  function baseQuery(scope: VnextListScope, head: boolean) {
    let query = db
      .from('contracts')
      .select(head ? 'id' : listColumns, { count: 'exact', head })
      .is('deleted_at', null)
      .or(kindFilter(scope.kind))
    const term = sanitizeSearch(scope.search)
    if (term) query = query.or(`numero.ilike.%${term}%,billing_name.ilike.%${term}%,saison.ilike.%${term}%`)
    return applyContractDisplayFilters(query, scope)
  }

  return {
    async list(filters: VnextListFilters) {
      const from = (filters.page - 1) * filters.pageSize
      const query = baseQuery(filters, false)
        .order('updated_at', { ascending: false })
        .order('id')
        .range(from, from + filters.pageSize - 1)
      const result = (await query) as unknown as PostgrestResult
      if (result.error) throw new Error(`VNEXT_LIST_FAILED:${result.error.code ?? 'UNKNOWN'}`)
      const rows = z.array(listRowSchema).parse(result.data)
      const vnextIds = rows.filter((row) => row.contract_flow === 'VNEXT').map((row) => row.id)
      const summaries = new Map<string, z.infer<typeof summarySchema>[number]>()
      if (vnextIds.length > 0) {
        const response = await db.rpc('vnext_contract_location_summaries_v1', { p_contract_ids: vnextIds })
        if (response.error) throw new Error(`VNEXT_SUMMARY_FAILED:${response.error.code}`)
        for (const summary of summarySchema.parse(response.data)) summaries.set(summary.contractId, summary)
      }
      const items = rows.map((row): VnextContractListItem => {
        const summary = summaries.get(row.id)
        const clientName =
          nonEmpty(row.client?.entreprise) ??
          nonEmpty([row.client?.prenom, row.client?.nom].filter(Boolean).join(' '))
        const contact = summary?.defaultContact ?? clientName
        return {
          id: row.id,
          number: row.numero,
          flow: row.contract_flow,
          company: nonEmpty(row.billing_name) ?? clientName ?? 'Entreprise non précisée',
          ...(contact ? { contact } : {}),
          ...(row.saison ? { season: row.saison } : {}),
          ...(present(row.prix) ? { priceCents: Math.round(row.prix * 100) } : {}),
          status: statusFromStatut[row.statut] ?? 'DRAFT',
          ...(row.status_v2 ? { statusV2: row.status_v2 as ContractStatusV2 } : {}),
          ...(row.date_signature ? { signedOn: row.date_signature } : {}),
          ...(row.sent_at ? { sentAt: row.sent_at } : {}),
          ...(row.opened_since_last_send ? { openedSinceLastSend: true } : {}),
          ...(row.sans_facturation ? { sansFacturation: true } : {}),
          ...(summary
            ? { locations: { total: summary.total, ready: summary.ready, assigned: summary.assigned } }
            : {}),
        }
      })
      return { items, total: result.count ?? items.length, source: 'supabase' }
    },

    async count(scope) {
      const result = (await baseQuery(scope, true)) as unknown as PostgrestResult
      if (result.error) throw new Error(`VNEXT_LIST_FAILED:${result.error.code ?? 'UNKNOWN'}`)
      return result.count ?? 0
    },

    async create(command) {
      const response = await db.rpc('create_vnext_contract_v1', { p_command: command })
      if (response.error) throw new Error(response.error.message || 'VNEXT_CREATE_FAILED')
      const result = createResultSchema.parse(response.data)
      return result
    },

    async locationsOverview(contractId) {
      const response = await db.rpc('contract_service_locations_overview_v1', { p_contract_id: contractId })
      if (response.error) throw new Error(`VNEXT_LOCATIONS_FAILED:${response.error.code}`)
      const parsed = overviewSchema.parse(response.data)
      const overview: VnextLocationsOverview = {
        contractId: parsed.contractId,
        contractType: parsed.contractType,
        ...(parsed.pricingMode ? { pricingMode: parsed.pricingMode } : {}),
        totals: parsed.totals,
        locations: parsed.locations.map((location) => ({
          id: location.id,
          order: location.order,
          formattedAddress: location.formattedAddress,
          line1: location.line1,
          ...(location.city ? { city: location.city } : {}),
          ...(location.postalCode ? { postalCode: location.postalCode } : {}),
          ...(present(location.latitude) && present(location.longitude)
            ? { latitude: location.latitude, longitude: location.longitude }
            : {}),
          ...(location.siteName ? { siteName: location.siteName } : {}),
          ...(location.siteType ? { siteType: location.siteType } : {}),
          ...(present(location.unitCount) ? { unitCount: location.unitCount } : {}),
          ...(present(location.parkingSpaces) ? { parkingSpaces: location.parkingSpaces } : {}),
          ...(present(location.priceCents) ? { priceCents: location.priceCents } : {}),
          ...(location.validationStatus ? { validationStatus: location.validationStatus } : {}),
          useContractDefaultContact: location.useContractDefaultContact,
          ...(location.contact
            ? {
                contact: {
                  source: location.contact.source,
                  ...(location.contact.firstName ? { firstName: location.contact.firstName } : {}),
                  ...(location.contact.lastName ? { lastName: location.contact.lastName } : {}),
                  ...(location.contact.phone ? { phone: location.contact.phone } : {}),
                  ...(location.contact.roleLabel ? { roleLabel: location.contact.roleLabel } : {}),
                },
              }
            : {}),
          ...(location.route ? { route: location.route } : {}),
          readiness: location.readiness,
        })),
      }
      return overview
    },

    async flowOf(reference) {
      const base = db.from('contracts').select('id,numero,contract_flow').is('deleted_at', null)
      const query = uuidPattern.test(reference) ? base.eq('id', reference) : base.eq('numero', reference)
      const result = (await query.maybeSingle()) as unknown as PostgrestResult
      if (result.error) throw new Error(`VNEXT_FLOW_FAILED:${result.error.code ?? 'UNKNOWN'}`)
      if (!result.data) return null
      const row = flowRowSchema.parse(result.data)
      return { id: row.id, number: row.numero, flow: row.contract_flow }
    },

    async defaultContact(contractId) {
      const result = (await db
        .from('contract_contacts')
        .select('first_name,last_name,phone,phone_secondary,email,role_label,is_emergency')
        .eq('contract_id', contractId)
        .eq('kind', 'DEFAULT_ON_SITE')
        .maybeSingle()) as unknown as PostgrestResult
      if (result.error) throw new Error(`VNEXT_CONTACT_FAILED:${result.error.code ?? 'UNKNOWN'}`)
      if (!result.data) return null
      const row = contactRowSchema.parse(result.data)
      return {
        ...(row.first_name ? { firstName: row.first_name } : {}),
        ...(row.last_name ? { lastName: row.last_name } : {}),
        ...(row.phone ? { phone: row.phone } : {}),
        ...(row.phone_secondary ? { phoneSecondary: row.phone_secondary } : {}),
        ...(row.email ? { email: row.email } : {}),
        ...(row.role_label ? { roleLabel: row.role_label } : {}),
        isEmergency: row.is_emergency,
      }
    },

    async locationDetail(locationId) {
      const response = await db.rpc('service_location_detail_v1', { p_location_id: locationId })
      if (response.error) throw new Error(response.error.message || 'VNEXT_LOCATION_FAILED')
      const parsed = detailSchema.parse(response.data)
      return withoutNulls({
        ...parsed,
        contacts: parsed.contacts.map((contact) => withoutNulls(contact)),
      })
    },

    async updateLocation(command) {
      const response = await db.rpc('update_service_location_vnext_v1', { p_command: command })
      if (response.error) throw new Error(response.error.message || 'VNEXT_LOCATION_UPDATE_FAILED')
      return writeResultSchema.parse(response.data).readiness
    },

    async copySettings(command) {
      const response = await db.rpc('copy_service_location_settings_v1', { p_command: command })
      if (response.error) throw new Error(response.error.message || 'VNEXT_COPY_FAILED')
      return z.object({ copied: z.number().int() }).parse(response.data).copied
    },

    async replaceEntries(locationId, entries) {
      const response = await db.rpc('replace_service_location_entries_v1', {
        p_command: { locationId, entries },
      })
      if (response.error) throw new Error(response.error.message || 'VNEXT_ENTRIES_FAILED')
      return writeResultSchema.parse(response.data).readiness
    },

    async replaceZones(command) {
      const response = await db.rpc('replace_service_location_zones_v2', {
        p_command: {
          idempotencyKey: command.idempotencyKey,
          locationId: command.locationId,
          expectedRevision: command.expectedRevision,
          zones: command.zones.map((zone) => ({
            label: zone.label,
            kind: zone.kind,
            geometry: zone.geometry,
          })),
        },
      })
      if (response.error) throw new Error(response.error.message || 'VNEXT_ZONES_FAILED')
      const parsed = writeResultSchema
        .extend({ revision: z.number().int().nonnegative() })
        .parse(response.data)
      return { readiness: parsed.readiness, revision: parsed.revision }
    },

    async zonesRevision(locationId) {
      const response = await db.rpc('service_location_zones_state_v1', { p_location_id: locationId })
      if (response.error) throw new Error(response.error.message || 'VNEXT_ZONES_STATE_FAILED')
      return z.object({ revision: z.number().int().nonnegative() }).parse(response.data).revision
    },

    async conversionState(contractId) {
      const response = await db.rpc('contract_conversion_state_v1', { p_contract_id: contractId })
      if (response.error) throw new Error(response.error.message || 'CONVERSION_STATE_FAILED')
      return withoutNulls(conversionStateSchema.parse(response.data))
    },

    async startConversion(sourceContractId, conversionType) {
      const response = await db.rpc('start_contract_conversion_v1', {
        p_command: { sourceContractId, conversionType },
      })
      if (response.error) throw new Error(response.error.message || 'CONVERSION_START_FAILED')
      return z.object({ conversionId: z.uuid() }).parse(response.data).conversionId
    },

    async completeConversion(conversionId, targetContractId) {
      const response = await db.rpc('complete_contract_conversion_v1', {
        p_command: { conversionId, targetContractId },
      })
      if (response.error) throw new Error(response.error.message || 'CONVERSION_COMPLETE_FAILED')
    },

    async abandonConversion(conversionId) {
      const response = await db.rpc('abandon_contract_conversion_v1', { p_command: { conversionId } })
      if (response.error) throw new Error(response.error.message || 'CONVERSION_ABANDON_FAILED')
    },

    async finalizeConversion(contractId) {
      const response = await db.rpc('finalize_contract_conversion_v1', { p_command: { contractId } })
      if (response.error) throw new Error(response.error.message || 'CONVERSION_FINALIZE_FAILED')
    },

    async changeType(contractId, targetType) {
      const response = await db.rpc('change_contract_type_v1', { p_command: { contractId, targetType } })
      if (response.error) throw new Error(response.error.message || 'CONTRACT_TYPE_CHANGE_FAILED')
    },
  }
}
